import React, { useMemo } from 'react';
import { 
  BarChart3, Users, PieChart, TrendingUp, 
  Calendar, Star, Map, Heart, Cake, MapPin, Activity,
  Briefcase, GraduationCap, PhoneOff, UserPlus, Ghost, Droplets, User
} from 'lucide-react';
import { FamilyMember } from '../types';

interface FamilyStatsProps {
  members: Record<string, FamilyMember>;
}

// Helper: Calculate Age
const calculateAge = (dob: string | undefined, fallbackAge: number | undefined) => {
  if (!dob) return Number(fallbackAge) || 0;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

const FamilyStats: React.FC<FamilyStatsProps> = ({ members }) => {
  // Safety check - if members are missing, show loading state
  const stats = useMemo(() => {
    if (!members || Object.keys(members).length === 0) {
      return {
        total: 0, living: 0, deceased: 0, invited: 0, noNumber: 0,
        byGender: { Male: 0, Female: 0, Other: 0 },
        byGeneration: {} as Record<number, number>,
        byLocation: {} as Record<string, number>,
        ageDistribution: { '0-18': 0, '19-35': 0, '36-50': 0, '51-70': 0, '71+': 0 },
        topLocations: [] as { name: string; count: number }[],
        topProfessions: [] as { name: string; count: number }[],
        averageAge: 0
      };
    }
    const list = Object.values(members);
    const total = list.length;
    
    // --- 1. BASIC COUNTERS (Legacy) ---
    let living = 0, deceased = 0, invited = 0, noNumber = 0;
    let singleMen = 0, singleWomen = 0;
    let totalAge = 0, ageCount = 0;

    // --- 2. AGGREGATIONS (Legacy + New) ---
    const bloodGroups: Record<string, number> = {};
    const locations: Record<string, number> = {};
    const professions: Record<string, number> = {};
    const education: Record<string, number> = {};
    const names: Record<string, number> = {};
    const zodiacs: Record<string, number> = {};
    const monthCounts = new Array(12).fill(0);

    // --- 3. AGE GROUPS (Legacy) ---
    const ageGroups = {
        '0-18 (Kids)': { m: 0, f: 0, total: 0 },
        '19-35 (Youth)': { m: 0, f: 0, total: 0 },
        '36-59 (Adults)': { m: 0, f: 0, total: 0 },
        '60-79 (Seniors)': { m: 0, f: 0, total: 0 },
        '80+ (Super Seniors)': { m: 0, f: 0, total: 0 }
    };

    // --- 4. OLDEST TRACKERS (Legacy) ---
    let oldestMale: { name: string; age: number; avatarUrl?: string } | null = null;
    let oldestFemale: { name: string; age: number; avatarUrl?: string } | null = null;

    list.forEach(m => {
        // Basic Status
        if (m.isDeceased) deceased++;
        else {
            living++;
            if (!m.joined) {
                if (m.phoneNumber && m.phoneNumber.length > 5) invited++;
                else noNumber++;
            }
        }

        // Names
        if (m.name) {
            const firstName = m.name.split(' ')[0];
            names[firstName] = (names[firstName] || 0) + 1;
        }

        // Blood Group
        if (m.bloodGroup) {
            const bg = m.bloodGroup.trim(); 
            bloodGroups[bg] = (bloodGroups[bg] || 0) + 1;
        }

        // Location
        if (m.location) {
            const city = m.location.split(',')[0].trim();
            locations[city] = (locations[city] || 0) + 1;
        }

        // Profession & Education
        if (m.profession) {
            const job = m.profession.trim();
            professions[job] = (professions[job] || 0) + 1;
        }
        if (m.education) {
            const degree = m.education.trim();
            education[degree] = (education[degree] || 0) + 1;
        }

        // --- DEMOGRAPHICS & DATES ---
        if (!m.isDeceased) {
            // Age Calculation
            let age = 0;
            if (m.dob) {
                age = calculateAge(m.dob, undefined);
                // Birthday Month Logic
                const d = new Date(m.dob);
                if (!isNaN(d.getTime())) monthCounts[d.getMonth()]++;
                
                // Zodiac Logic
                const month = d.getMonth() + 1;
                const day = d.getDate();
                let sign = '';
                if((month == 1 && day <= 20) || (month == 12 && day >=22)) sign = "Capricorn";
                else if((month == 1 && day >= 21) || (month == 2 && day <= 18)) sign = "Aquarius";
                else if((month == 2 && day >= 19) || (month == 3 && day <= 20)) sign = "Pisces";
                else if((month == 3 && day >= 21) || (month == 4 && day <= 19)) sign = "Aries";
                else if((month == 4 && day >= 20) || (month == 5 && day <= 20)) sign = "Taurus";
                else if((month == 5 && day >= 21) || (month == 6 && day <= 20)) sign = "Gemini";
                else if((month == 6 && day >= 21) || (month == 7 && day <= 22)) sign = "Cancer";
                else if((month == 7 && day >= 23) || (month == 8 && day <= 22)) sign = "Leo";
                else if((month == 8 && day >= 23) || (month == 9 && day <= 22)) sign = "Virgo";
                else if((month == 9 && day >= 23) || (month == 10 && day <= 22)) sign = "Libra";
                else if((month == 10 && day >= 23) || (month == 11 && day <= 21)) sign = "Scorpio";
                else if((month == 11 && day >= 22) || (month == 12 && day >= 21)) sign = "Sagittarius";
                zodiacs[sign] = (zodiacs[sign] || 0) + 1;
            }

            if (age > 0 && age < 120) {
                totalAge += age;
                ageCount++;

                // Age Buckets
                let bucket = '80+ (Super Seniors)';
                if (age <= 18) bucket = '0-18 (Kids)';
                else if (age <= 35) bucket = '19-35 (Youth)';
                else if (age <= 59) bucket = '36-59 (Adults)';
                else if (age <= 79) bucket = '60-79 (Seniors)';

                if (ageGroups[bucket as keyof typeof ageGroups]) {
                    ageGroups[bucket as keyof typeof ageGroups].total++;
                    if (m.gender === 'Male') ageGroups[bucket as keyof typeof ageGroups].m++;
                    else if (m.gender === 'Female') ageGroups[bucket as keyof typeof ageGroups].f++;
                }

                // Oldest Trackers
                if (m.gender === 'Male') {
                    if (!oldestMale || age > oldestMale.age) oldestMale = { name: m.name, age, avatarUrl: m.avatarUrl };
                }
                if (m.gender === 'Female') {
                    if (!oldestFemale || age > oldestFemale.age) oldestFemale = { name: m.name, age, avatarUrl: m.avatarUrl };
                }

                // Singles - check maritalStatus with fallback to isMarried
                const isSingle = m.maritalStatus === 'Single' || (!m.maritalStatus && !m.isMarried);
                if (isSingle && age >= 22) {
                    if (m.gender === 'Male') singleMen++;
                    else if (m.gender === 'Female') singleWomen++;
                }
            }
        }
    });

    // --- 5. SORT & CALCULATE FINALS ---
    const getSorted = (obj: Record<string, number>) => Object.entries(obj).sort((a, b) => b[1] - a[1]);
    const avgAge = ageCount > 0 ? Math.round(totalAge / ageCount) : 0;
    
    // Gender Pct
    const males = list.filter(m => m.gender === 'Male').length;
    const females = list.filter(m => m.gender === 'Female').length;
    const malePct = total ? Math.round((males / total) * 100) : 0;
    const femalePct = total ? Math.round((females / total) * 100) : 0;

    const topZodiac = Object.entries(zodiacs).sort((a,b) => b[1] - a[1])[0] || ['None', 0];
    const topNames = getSorted(names).slice(0, 5);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const maxMonthValue = Math.max(...monthCounts) || 1;

    return { 
        total, living, deceased, invited, noNumber, 
        singleMen, singleWomen, avgAge,
        malePct, femalePct,
        oldestMale, oldestFemale,
        ageGroups,
        sortedBloodGroups: getSorted(bloodGroups),
        sortedCurrentCities: getSorted(locations),
        sortedProfessions: getSorted(professions),
        sortedEducation: getSorted(education),
        topNames, topZodiac,
        monthCounts, months, maxMonthValue
    };
  }, [members]);

  const getPercent = (val: number, total: number) => Math.round((val / total) * 100) || 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-24 pt-6 px-4 w-full overflow-x-hidden">
      
      {/* HEADER CARD */}
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
           <h2 className="text-3xl font-black text-gray-900 flex items-center gap-2"><Activity className="w-8 h-8 text-amber-500" /> Family DNA</h2>
           <p className="text-gray-500 mt-1">Analyzing {stats.total} members across 4 generations.</p>
        </div>
        <div className="flex gap-4">
           <div className="text-center px-6 py-3 bg-indigo-50 rounded-2xl">
              <div className="text-2xl font-black text-indigo-600">{stats.total}</div>
              <div className="text-xs font-bold text-gray-400 uppercase">Members</div>
           </div>
           <div className="text-center px-6 py-3 bg-emerald-50 rounded-2xl">
              <div className="text-2xl font-black text-emerald-600">{stats.avgAge}</div>
              <div className="text-xs font-bold text-gray-400 uppercase">Avg Age</div>
           </div>
        </div>
      </div>

      {/* 1. KEY STATUS METRICS (Legacy Cards - Restyled) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center justify-center text-center">
            <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mb-2"><UserPlus className="w-5 h-5"/></div>
            <span className="text-2xl font-black text-gray-900">{stats.invited}</span>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Can Invite</span>
        </div>
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center justify-center text-center">
            <div className="w-10 h-10 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center mb-2"><PhoneOff className="w-5 h-5"/></div>
            <span className="text-2xl font-black text-gray-900">{stats.noNumber}</span>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">No Number</span>
        </div>
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center justify-center text-center">
            <div className="w-10 h-10 bg-slate-100 text-slate-600 rounded-full flex items-center justify-center mb-2"><Ghost className="w-5 h-5"/></div>
            <span className="text-2xl font-black text-gray-900">{stats.deceased}</span>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Departed</span>
        </div>
        <div className="bg-rose-50 p-4 rounded-2xl border border-rose-100 flex flex-col items-center justify-center text-center">
            <div className="w-10 h-10 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center mb-2"><Heart className="w-5 h-5"/></div>
            <span className="text-2xl font-black text-gray-900">{stats.singleMen + stats.singleWomen}</span>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Singles (22+)</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* LEFT COL: Demographics */}
        <div className="space-y-6">
            
            {/* Gender Split (Legacy) */}
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-6">
                    <PieChart className="w-5 h-5 text-indigo-500" /> Gender Balance
                </h3>
                <div className="flex items-center gap-4 mb-2">
                    <span className="text-sm font-bold text-blue-500 w-12">{stats.malePct}%</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden flex">
                        <div className="bg-blue-500 h-full" style={{ width: `${stats.malePct}%` }}></div>
                        <div className="bg-pink-500 h-full" style={{ width: `${stats.femalePct}%` }}></div>
                    </div>
                    <span className="text-sm font-bold text-pink-500 w-12 text-right">{stats.femalePct}%</span>
                </div>
            </div>

            {/* Age Groups (Legacy) */}
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-6">
                    <Users className="w-5 h-5 text-emerald-500" /> Generation Ladder
                </h3>
                <div className="space-y-5">
                    {Object.entries(stats.ageGroups).map(([label, data]) => {
                        if (data.total === 0) return null;
                        const mPct = getPercent(data.m, data.total);
                        const fPct = getPercent(data.f, data.total);
                        return (
                            <div key={label}>
                                <div className="flex justify-between text-xs font-bold mb-1.5 text-gray-700">
                                    <span>{label}</span>
                                    <span className="bg-gray-100 px-2 py-0.5 rounded text-[10px]">{data.total}</span>
                                </div>
                                <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden flex">
                                    <div style={{ width: `${mPct}%` }} className="h-full bg-blue-400"></div>
                                    <div style={{ width: `${fPct}%` }} className="h-full bg-pink-400"></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Oldest Living (Legacy) */}
            <div className="grid grid-cols-2 gap-3">
                {stats.oldestMale && (
                    <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-4 rounded-2xl shadow-md text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-12 h-12 bg-white/10 rounded-full -mr-4 -mt-4"></div>
                        <span className="text-[9px] uppercase font-bold text-blue-200 tracking-wider mb-2 block">Oldest Male</span>
                        <div className="flex items-center gap-2">
                            <img src={stats.oldestMale.avatarUrl || `https://ui-avatars.com/api/?name=${stats.oldestMale.name}`} className="w-8 h-8 rounded-full border border-white/30 object-cover" />
                            <div className="overflow-hidden">
                                <p className="text-xs font-bold leading-tight truncate">{stats.oldestMale.name}</p>
                                <p className="text-[9px] text-blue-200">{stats.oldestMale.age} Years</p>
                            </div>
                        </div>
                    </div>
                )}
                {stats.oldestFemale && (
                    <div className="bg-gradient-to-br from-pink-500 to-rose-600 p-4 rounded-2xl shadow-md text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-12 h-12 bg-white/10 rounded-full -mr-4 -mt-4"></div>
                        <span className="text-[9px] uppercase font-bold text-pink-200 tracking-wider mb-2 block">Oldest Female</span>
                        <div className="flex items-center gap-2">
                            <img src={stats.oldestFemale.avatarUrl || `https://ui-avatars.com/api/?name=${stats.oldestFemale.name}`} className="w-8 h-8 rounded-full border border-white/30 object-cover" />
                            <div className="overflow-hidden">
                                <p className="text-xs font-bold leading-tight truncate">{stats.oldestFemale.name}</p>
                                <p className="text-[9px] text-pink-200">{stats.oldestFemale.age} Years</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* RIGHT COL: Fun & Lists */}
        <div className="space-y-6">

            {/* Zodiac (Legacy) */}
            <div className="bg-gradient-to-br from-indigo-900 to-slate-900 p-6 rounded-3xl shadow-lg text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-10 -mt-10"></div>
                <h3 className="font-bold text-indigo-200 flex items-center gap-2 mb-2 relative z-10">
                    <Star className="w-5 h-5" /> Star Sign
                </h3>
                <div className="mt-4 relative z-10">
                    <div className="text-4xl font-black">{stats.topZodiac[0]}</div>
                    <p className="text-indigo-300 text-sm mt-1">{stats.topZodiac[1]} members share this sign.</p>
                </div>
            </div>

            {/* NEW: Birthday Seasonality */}
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-6">
                    <Cake className="w-5 h-5 text-pink-500" /> Birthday Seasonality
                </h3>
                {stats.monthCounts.some(c => c > 0) ? (
                    <div className="flex items-end gap-1 h-24 w-full">
                        {stats.monthCounts.map((count, idx) => (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-1 group">
                                <div 
                                    className="w-full bg-pink-100 rounded-t-md relative hover:bg-pink-400 transition-colors group-hover:bg-pink-400" 
                                    style={{ height: count > 0 ? `${(count / stats.maxMonthValue) * 100}%` : '2px', minHeight: count > 0 ? '4px' : '2px' }}
                                    title={`${stats.months[idx]}: ${count} birthday(s)`}
                                ></div>
                                <span className="text-[8px] font-bold text-gray-400 uppercase">{stats.months[idx][0]}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-24 text-gray-400 text-xs font-medium">
                        Add members with birth dates to see birthday distribution
                    </div>
                )}
            </div>

            {/* LISTS: Profession & Education (Legacy) */}
            <div className="bg-white p-5 rounded-3xl shadow-sm border border-gray-100 grid grid-cols-2 gap-4">
                <div>
                    <h3 className="text-xs font-black text-gray-900 flex items-center gap-2 mb-3 uppercase tracking-wide">
                        <GraduationCap className="w-3.5 h-3.5 text-purple-500" /> Education
                    </h3>
                    <ul className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                        {stats.sortedEducation.length > 0 ? stats.sortedEducation.map(([edu, count]) => (
                            <li key={edu} className="flex justify-between items-center text-xs">
                                <span className="font-medium text-gray-600 truncate max-w-[70%]">{edu}</span>
                                <span className="bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded-md font-bold text-[10px]">{count}</span>
                            </li>
                        )) : <li className="text-[10px] text-gray-400 italic">No data</li>}
                    </ul>
                </div>
                <div>
                    <h3 className="text-xs font-black text-gray-900 flex items-center gap-2 mb-3 uppercase tracking-wide">
                        <Briefcase className="w-3.5 h-3.5 text-blue-500" /> Profession
                    </h3>
                    <ul className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                        {stats.sortedProfessions.length > 0 ? stats.sortedProfessions.map(([job, count]) => (
                            <li key={job} className="flex justify-between items-center text-xs">
                                <span className="font-medium text-gray-600 truncate max-w-[70%]">{job}</span>
                                <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-md font-bold text-[10px]">{count}</span>
                            </li>
                        )) : <li className="text-[10px] text-gray-400 italic">No data</li>}
                    </ul>
                </div>
            </div>

            {/* LISTS: Blood & Location (Legacy + New) */}
            <div className="bg-white p-5 rounded-3xl shadow-sm border border-gray-100 grid grid-cols-2 gap-4">
                <div>
                    <h3 className="text-xs font-black text-gray-900 flex items-center gap-2 mb-3 uppercase tracking-wide">
                        <Droplets className="w-3.5 h-3.5 text-red-500" /> Blood Group
                    </h3>
                    <ul className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                        {stats.sortedBloodGroups.length > 0 ? stats.sortedBloodGroups.map(([bg, count]) => (
                            <li key={bg} className="flex justify-between items-center text-xs">
                                <span className="font-medium text-gray-600">{bg}</span>
                                <span className="bg-red-50 text-red-600 px-1.5 py-0.5 rounded-md font-bold text-[10px]">{count}</span>
                            </li>
                        )) : <li className="text-[10px] text-gray-400 italic">No data</li>}
                    </ul>
                </div>
                <div>
                    <h3 className="text-xs font-black text-gray-900 flex items-center gap-2 mb-3 uppercase tracking-wide">
                        <MapPin className="w-3.5 h-3.5 text-emerald-500" /> Location
                    </h3>
                    <ul className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                        {stats.sortedCurrentCities.length > 0 ? stats.sortedCurrentCities.map(([city, count]) => (
                            <li key={city} className="flex justify-between items-center text-xs">
                                <span className="font-medium text-gray-600 truncate max-w-[70%]">{city}</span>
                                <span className="bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-md font-bold text-[10px]">{count}</span>
                            </li>
                        )) : <li className="text-[10px] text-gray-400 italic">No data</li>}
                    </ul>
                </div>
            </div>

        </div>
      </div>
    </div>
  );
};

export default FamilyStats;